import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Download, FileText, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { PrintToPDF } from './print-to-pdf';
import { PrintService } from '../../services/print-service';

interface SurveyCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surveyData: {
    id: number;
    customerName: string;
    installationAddress: string;
  } | null;
}

export function SurveyCompletionDialog({
  open,
  onOpenChange,
  surveyData,
}: SurveyCompletionDialogProps) {
  const [, setLocation] = useLocation();
  const [countdown, setCountdown] = useState(5);
  const [autoRedirect, setAutoRedirect] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !autoRedirect || !surveyData) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Redirect to survey management with highlight parameter
          setLocation(`/survey-management?highlight=${surveyData.id}&from=completion`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, autoRedirect, surveyData, setLocation]);

  if (!surveyData) return null;

  const handleViewInManagement = () => {
    setLocation(`/survey-management?highlight=${surveyData.id}&from=completion`);
  };

  const handleCreateAnother = () => {
    setLocation('/site-surveys');
  };

  const handleStayHere = () => {
    setAutoRedirect(false);
    setCountdown(5);
  };

  const handleDownloadPdf = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      // Use the new print service for PDF generation
      await PrintService.printSurvey(surveyData.id.toString());

      toast({
        title: 'Print Dialog Opened',
        description: "Use your browser's print dialog to save as PDF or print.",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Download Failed',
        description:
          error instanceof Error ? error.message : 'Failed to generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <DialogTitle>Survey Completed Successfully!</DialogTitle>
              <DialogDescription className="mt-1">
                Site survey for {surveyData.customerName} has been saved
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-gray-50 p-3">
            <p className="text-sm font-medium text-gray-900">Survey Details</p>
            <p className="text-sm text-gray-600 mt-1">{surveyData.installationAddress}</p>
          </div>

          {autoRedirect && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  Redirecting to Survey Management in {countdown}s
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleStayHere}
                  className="h-auto p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Progress value={(5 - countdown) * 20} className="h-2" />
            </div>
          )}

          <div className="space-y-2">
            <PrintToPDF
              surveyId={surveyData.id.toString()}
              buttonText="Download PDF Report"
              buttonVariant="outline"
              className="w-full"
              onPrintError={(error) => {
                toast({
                  title: 'Download Failed',
                  description: error.message || 'Failed to generate PDF.',
                  variant: 'destructive',
                });
              }}
            />
            <Button onClick={handleViewInManagement} variant="outline" className="w-full">
              <FileText className="mr-2 h-4 w-4" />
              View in Survey Management
            </Button>
            <Button onClick={handleCreateAnother} variant="outline" className="w-full">
              <Plus className="mr-2 h-4 w-4" />
              Create Another Survey
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
